/**
 * Dynamic Segmentation API Service
 * Handles all API calls for dynamic persona segmentation
 */

const API_URL = import.meta.env.VITE_API_URL || '';

export interface SegmentationParams {
  audience_id: number;
  segmentation_axis: string;
  num_segments?: number; // Optional: LLM auto-determines optimal number
  segment_ids?: number[]; // Optional: limit to specific segments
  force_regenerate?: boolean; // Skip cache
  use_v2?: boolean; // Use v2 segmentation pipeline with attribute extraction
}

export interface DynamicSegment {
  index: number;
  label: string;
  description: string;
  persona_count: number;
  persona_ids: number[];
  defining_characteristics?: string[];
}

export interface Segmentation {
  id: number;
  audience_id: number;
  user_id: number;
  name: string;
  segmentation_axis: string;
  segmentation_description?: string;
  num_segments: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  segment_definitions: DynamicSegment[];
  persona_assignments: PersonaAssignment[];
  analysis_summary?: string;
  error_message?: string;
  total_personas_analyzed: number;
  processing_time_ms?: number;
  source_model?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface PersonaAssignment {
  persona_id: number;
  segment_index: number;
  segment_label: string;
  confidence: number;
  reasoning?: string;
  key_indicators?: string[];
}

export interface SegmentationResponse {
  success: boolean;
  segmentation_id: number;
  task_id?: string; // Present if async
  cached?: boolean;
  original_axis?: string; // Present if cache match
  segments?: DynamicSegment[]; // Present if sync/cached
  message?: string;
}

export interface SegmentationStatusResponse {
  segmentation_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  current_step?: string;
  error_message?: string;
}

export interface SuggestedAxis {
  id: number;
  axis: string;
  last_used: string;
  times_used: number;
}

export const segmentationService = {
  /**
   * Create new segmentation (may be sync or async based on persona count)
   */
  async create(params: SegmentationParams): Promise<SegmentationResponse> {
    const response = await fetch(`${API_URL}/segmentation/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    return response.json();
  },

  /**
   * Create segmentation always async via Celery
   */
  async createAsync(params: SegmentationParams): Promise<SegmentationResponse> {
    const response = await fetch(`${API_URL}/segmentation/create-async`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    return response.json();
  },

  /**
   * Get segmentation by ID
   */
  async get(id: number): Promise<Segmentation> {
    const response = await fetch(`${API_URL}/segmentation/${id}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    return response.json();
  },

  /**
   * Get segmentation status (for polling)
   */
  async getStatus(id: number): Promise<SegmentationStatusResponse> {
    const response = await fetch(`${API_URL}/segmentation/${id}/status`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    return response.json();
  },

  /**
   * List segmentations for audience
   */
  async listForAudience(audienceId: number): Promise<Segmentation[]> {
    const response = await fetch(`${API_URL}/segmentation/audience/${audienceId}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.segmentations || [];
  },

  /**
   * Get personas for a specific segment.
   *
   * The endpoint hydrates at most a fixed number of persona objects but reports the
   * segment's true `total`, so callers can show "showing N of M" instead of mistaking the
   * preview cap for the segment size.
   */
  async getPersonasBySegment(
    segmentationId: number,
    segmentIndex: number,
  ): Promise<{ personas: any[]; total: number; returned: number }> {
    const response = await fetch(
      `${API_URL}/segmentation/${segmentationId}/personas/full?segment_index=${segmentIndex}`,
      { credentials: 'include' },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    // Backward-compatible with the legacy bare-array response shape.
    if (Array.isArray(data)) {
      return { personas: data, total: data.length, returned: data.length };
    }
    return {
      personas: data.personas ?? [],
      total: data.total ?? data.personas?.length ?? 0,
      returned: data.returned ?? data.personas?.length ?? 0,
    };
  },

  /**
   * Get suggested axes for audience (previously used)
   */
  async getSuggestedAxes(audienceId: number): Promise<SuggestedAxis[]> {
    const response = await fetch(`${API_URL}/segmentation/audience/${audienceId}/suggested-axes`, {
      credentials: 'include',
    });

    if (!response.ok) {
      // Return empty array on error (not critical)
      return [];
    }

    const data = await response.json();
    return data.suggested_axes || [];
  },

  /**
   * Delete segmentation
   */
  async delete(id: number): Promise<void> {
    const response = await fetch(`${API_URL}/segmentation/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || `HTTP ${response.status}`);
    }
  },
};

export default segmentationService;
